import Axios from "../../../lib/Axios";

/**
 * Hotspot Moderation API Helper
 * 
 * This file contains all API calls related to Hotspot Moderation.
 * Update the endpoints when backend APIs are ready.
 */

/**
 * Fetch all hotspot moderation records with filters
 * @param {Object} filters - Filter parameters
 * @param {string} filters.status - Status filter: 'pending', 'rejected', 'approved', or undefined (all)
 * @param {string} filters.type - Type filter: 'personal', 'community', or undefined (both)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.keyword - Search keyword
 * @param {string} filters.dateRange - Date range: 'last_24_hours', 'last_7_days', 'last_30_days', or undefined
 * @param {string} filters.startDate - Start date in YYYY-MM-DD format
 * @param {string} filters.endDate - End date in YYYY-MM-DD format
 * @returns {Promise} API response - Returns array of hotspot objects
 */
export const getHotspotModerationList = async (filters = {}) => {
  const {
    status,
    type,
    limit = 50,
    page = 1,
    keyword,
    dateRange,
    startDate,
    endDate,
    is_suspended,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (status) params.status = status;
  if (type) params.type = type;
  if (keyword) params.keyword = keyword;
  if (dateRange) params.dateRange = dateRange;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (is_suspended !== undefined && is_suspended !== "") {
    params.is_suspended = is_suspended === "true" ? "true" : "false";
  }

  console.log("API Params:", params);

  const result = await Axios.get("admin/tag-requests", { params });
  const data = result?.data?.result?.tag_requests || [];
  const pagination = result?.data?.pagination || {};
  return {
    data,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Create a new hotspot moderation record
 * @param {Object} data - Hotspot moderation data
 * @returns {Promise} API response
 */
export const createHotspotModeration = async (data) => {
  // TODO: Update endpoint when backend API is ready
  // Expected endpoint: POST /cms/cms-set/hotspot-moderation
  const result = await Axios.post("/cms/cms-set/hotspot-moderation", data);
  return result;
};

/**
 * Update an existing hotspot moderation record
 * @param {Object} data - Hotspot moderation data with ID
 * @returns {Promise} API response
 */
export const updateHotspotModeration = async (data) => {
  // TODO: Update endpoint when backend API is ready
  // Expected endpoint: POST /cms/cms-edit/hotspot-moderation
  const result = await Axios.post("/cms/cms-edit/hotspot-moderation", data);
  return result;
};

/**
 * Delete hotspot moderation record(s)
 * @param {Object} data - Object containing array of hotspot IDs to delete
 * @returns {Promise} API response
 */
export const deleteHotspotModeration = async (data) => {
  // TODO: Update endpoint when backend API is ready
  // Expected endpoint: POST /cms/cms-delete/hotspot-moderation
  const result = await Axios.post("/cms/cms-delete/hotspot-moderation", data);
  return result;
};

/**
 * Approve a hotspot
 * @param {Object} data - Hotspot data with request_id
 * @param {number} data.request_id - The request ID to approve
 * @returns {Promise} API response
 */
export const approveHotspot = async (data) => {
  const { request_id } = data;
  if (!request_id) {
    throw new Error("request_id is required");
  }
  
  // POST /admin/tag-requests/{request_id}/approve
  const result = await Axios.post(`admin/tag-requests/${request_id}/approve`);
  return result;
};

/**
 * Reject a hotspot
 * @param {Object} data - Hotspot data with request_id and rejection_reason object
 * @param {number} data.request_id - The request ID to reject
 * @param {Object} data.rejection_reason - Rejection reason object
 * @param {string} data.rejection_reason.by_admin - Admin ID who is rejecting
 * @param {string} data.rejection_reason.timestamp - ISO timestamp
 * @param {Array<string>} data.rejection_reason.reasons - Array of rejection reasons
 * @param {string} data.rejection_reason.comment - Additional comment
 * @returns {Promise} API response
 */
export const rejectHotspot = async (data) => {
  const { request_id, rejection_reason } = data;
  if (!request_id) {
    throw new Error("request_id is required");
  }
  if (!rejection_reason) {
    throw new Error("rejection_reason is required");
  }
  
  // POST /admin/tag-requests/{request_id}/reject
  // Body: { rejection_reason: { by_admin, timestamp, reasons[], comment } }
  const result = await Axios.post(`admin/tag-requests/${request_id}/reject`, {
    rejection_reason: rejection_reason,
  });
  return result;
};

/**
 * Get tag details by tag_id (for approved tags)
 * @param {number} tag_id - The tag ID
 * @returns {Promise} API response - Returns tag details object
 */
export const getTagDetails = async (tag_id) => {
  if (!tag_id) {
    throw new Error("tag_id is required");
  }
  
  // GET /admin/tag/{tag_id}
  const result = await Axios.get(`admin/tag/${tag_id}`);
  return result;
};

/**
 * Suspend a hotspot tag
 * @param {Object} data - Suspend data
 * @param {number} data.tag_id - The tag ID to suspend
 * @param {string} data.reason - Reason for suspension
 * @returns {Promise} API response
 */
export const suspendHotspot = async (data) => {
  const { tag_id, reason } = data;
  
  if (!tag_id) {
    throw new Error("tag_id is required");
  }
  if (!reason) {
    throw new Error("reason is required");
  }

  // POST /admin/tags/suspend
  const result = await Axios.post("admin/tags/suspend", {
    tag_id,
    reason,
  });
  
  return result;
};

/**
 * Update tag verification status
 * @param {Object} data - Verification data
 * @param {number} data.tag_id - The tag ID to verify
 * @param {boolean} data.is_verified - Verification status (true/false)
 * @returns {Promise} API response
 */
export const updateTagVerification = async (data) => {
  const { tag_id, is_verified } = data;
  
  if (!tag_id) {
    throw new Error("tag_id is required");
  }
  if (is_verified === undefined || is_verified === null) {
    throw new Error("is_verified is required");
  }

  // POST /admin/tags/update-verification
  const result = await Axios.post("admin/tags/update-verification", {
    tag_id,
    is_verified,
  });
  
  return result;
};

